import React, { useEffect, useState } from "react";
import axios from "axios";
import { ChevronUp, ChevronDown, MapPin } from "lucide-react";
import ImageModal from "../components/ImageModal";
import MapModal from "../components/MapModal";

const AdminDashboard = () => {
  const [userData, setUserData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sortConfig, setSortConfig] = useState({
    key: null,
    direction: 'ascending'
  });
  const [mapModal, setMapModal] = useState({
    isOpen: false,
    latitude: null,
    longitude: null
  });

  const [imageModal, setImageModal] = useState({
    isOpen: false,
    imageUrl: "",
    altText: "",
  });

  const backendURL = process.env.REACT_APP_BACKEND_URL;

  useEffect(() => {
    const fetchUserData = async () => {
      const token = localStorage.getItem("token");
      try {
        const response = await axios.get(
          `${process.env.REACT_APP_BACKEND_URL}/api/get-all-users`,
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          }
        );
        setUserData(response.data);
        setLoading(false);
      } catch (error) {
        console.error("Error fetching user data:", error);
        setLoading(false);
      }
    };

    fetchUserData();
  }, []);

  const handleSort = (key) => {
    let direction = 'ascending';
    if (sortConfig.key === key && sortConfig.direction === 'ascending') {
      direction = 'descending';
    }
    setSortConfig({ key, direction });

    const sortedData = [...userData].sort((a, b) => {
      let aValue = key.includes('.') ?
        key.split('.').reduce((obj, key) => obj[key], a) :
        a[key];
      let bValue = key.includes('.') ?
        key.split('.').reduce((obj, key) => obj[key], b) :
        b[key];

      if (key === 'timestamp') {
        aValue = new Date(aValue);
        bValue = new Date(bValue);
      }

      if (aValue < bValue) return direction === 'ascending' ? -1 : 1;
      if (aValue > bValue) return direction === 'ascending' ? 1 : -1;
      return 0;
    });

    setUserData(sortedData);
  };

  const getSortIcon = (key) => {
    if (sortConfig.key !== key) {
      return <ChevronUp className="w-4 h-4 text-gray-400" />;
    }
    return sortConfig.direction === 'ascending' ?
      <ChevronUp className="w-4 h-4 text-emerald-600" /> :
      <ChevronDown className="w-4 h-4 text-emerald-600" />;
  };

  const handleViewMap = (latitude, longitude) => {
    setMapModal({
      isOpen: true,
      latitude,
      longitude
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 flex items-center justify-center">
        <div className="text-blue-400 text-xl font-semibold">Loading...</div>
      </div>
    );
  }

  const columns = [
    { key: 'serial', label: 'S.No', notSortable: true },
    { key: 'timestamp', label: 'Timestamp' },
    { key: 'ipFromFrontend', label: 'IP (Frontend)' },
    { key: 'ipFromServer', label: 'IP (Server)' },
    { key: 'geo.latitude', label: 'Latitude' },
    { key: 'geo.longitude', label: 'Longitude' },
    { key: 'userAgent', label: 'User Agent' },
    { key: 'cameraImageFilename', label: 'Camera Image', notSortable: true },
    { key: 'screenshotFilename', label: 'Screenshot', notSortable: true },
    { key: 'actions', label: 'Actions', notSortable: true }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 p-8">
      <div className="max-w-7xl mx-auto">
        <h1 className="text-4xl font-bold text-white mb-8 text-center">
          Admin Dashboard
        </h1>
        <div className="bg-gray-800 rounded-2xl shadow-2xl overflow-hidden border border-gray-700">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-700">
                <tr>
                  {columns.map(({ key, label, notSortable }) => (
                    <th
                      key={key}
                      className="px-6 py-4 text-left text-sm font-semibold text-gray-200"
                    >
                      {notSortable ? (
                        <span>{label}</span>
                      ) : (
                        <button
                          onClick={() => handleSort(key)}
                          className="flex items-center space-x-1 hover:text-blue-400 transition-colors duration-200"
                        >
                          <span>{label}</span>
                          {getSortIcon(key)}
                        </button>
                      )}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-700">
                {userData.map((user, index) => (
                  <tr
                    key={index}
                    className="hover:bg-gray-700/50 transition-colors duration-150"
                  >
                    <td className="px-6 py-4 text-sm text-gray-300 whitespace-nowrap font-medium">
                      {index + 1}
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-300 whitespace-nowrap">
                      {new Date(user.timestamp).toLocaleString()}
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-300 whitespace-nowrap">
                      {user.ipFromFrontend}
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-300 whitespace-nowrap">
                      {user.ipFromServer}
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-300 whitespace-nowrap">
                      {user.geo.latitude}
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-300 whitespace-nowrap">
                      {user.geo.longitude}
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-300 max-w-xs truncate" title={user.userAgent}>
                      {user.userAgent}
                    </td>
                    <td className="px-6 py-4 text-sm whitespace-nowrap">
                      {user.cameraImageFilename ? (
                        <img
                          src={`${backendURL}/uploads/${user.cameraImageFilename}`}
                          alt="Camera"
                          className="h-16 w-16 rounded-lg object-cover cursor-pointer hover:opacity-80 transition-opacity duration-200"
                          onClick={() =>
                            setImageModal({
                              isOpen: true,
                              imageUrl: `${backendURL}/uploads/${user.cameraImageFilename}`,
                              altText: "Camera Image",
                            })
                          }
                        />
                      ) : (
                        <span className="text-gray-500">No Image</span>
                      )}
                    </td>
                    <td className="px-6 py-4 text-sm whitespace-nowrap">
                      {user.screenshotFilename ? (
                        <img
                          src={`${backendURL}/uploads/${user.screenshotFilename}`}
                          alt="Screenshot"
                          className="h-16 w-16 rounded-lg object-cover cursor-pointer hover:opacity-80 transition-opacity duration-200"
                          onClick={() =>
                            setImageModal({
                              isOpen: true,
                              imageUrl: `${backendURL}/uploads/${user.screenshotFilename}`,
                              altText: "Screenshot",
                            })
                          }
                        />
                      ) : (
                        <span className="text-gray-500">No Screenshot</span>
                      )}
                    </td>

                    <td className="px-6 py-4 text-sm whitespace-nowrap">
                      <button
                        onClick={() => handleViewMap(user.geo.latitude, user.geo.longitude)}
                        className="inline-flex items-center px-3 py-1.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors duration-200 shadow-md hover:shadow-lg"
                      >
                        <MapPin className="w-4 h-4 mr-1" />
                        View Map
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
      <ImageModal
        isOpen={imageModal.isOpen}
        onClose={() => setImageModal({ ...imageModal, isOpen: false })}
        imageUrl={imageModal.imageUrl}
        altText={imageModal.altText}
      />

      <MapModal
        isOpen={mapModal.isOpen}
        onClose={() => setMapModal({ ...mapModal, isOpen: false })}
        latitude={mapModal.latitude}
        longitude={mapModal.longitude}
      />
    </div>
  );
};

export default AdminDashboard;